import config from './index';
import { getTenantFromUrl as getTenantFromUrlService } from '../services/tenant.service';
import authService from '../services/auth.service';
import { REQUEST_TIMEOUT_DEFAULT_API } from './constants';

/**
 * Configuration validation error
 */
class ConfigurationError extends Error {
  constructor(message, missingValue) {
    super(message);
    this.name = 'ConfigurationError';
    this.missingValue = missingValue;
  }
}

/**
 * Get API base URL using dynamic configuration from config/index.js
 *
 * Uses the getApiBaseUrl() function from config/index.js which:
 * - Builds URLs dynamically based on hostname and environment
 * - Uses constants for ports (no env vars needed for localhost)
 * - Supports localhost, development, staging, and production environments
 *
 * REACT_APP_API_BASE_URL is no longer required - URLs are built dynamically.
 *
 * @returns {string} API base URL
 */
const getApiBaseUrl = () => {
  if (typeof window === 'undefined') {
    const errorMessage =
      'getApiBaseUrl() can only be called in browser context';
    throw new ConfigurationError(errorMessage, 'window');
  }

  // Use the dynamic getApiBaseUrl from config/index.js
  // This builds URLs based on hostname and environment, using constants for ports
  return config.getApiBaseUrl();
};

// Global tenant getter (can be set by AuthContext)
let globalTenantGetter = null;

/**
 * Set global tenant getter function (called by AuthContext)
 * @param {Function} getter - Function that returns tenant alias string
 */
export const setGlobalTenantGetter = getter => {
  globalTenantGetter = getter;
};

// CSRF token cache (fetched from API endpoint)
let csrfTokenCache = null;
let csrfTokenFetchPromise = null;

/**
 * Fetch CSRF token from API endpoint
 * This should be called on app initialization to get the token
 * @returns {Promise<string>} CSRF token
 */
export const fetchCsrfToken = async () => {
  // If we already have a fetch in progress, wait for it
  if (csrfTokenFetchPromise) {
    return csrfTokenFetchPromise;
  }

  // If we have a cached token, return it
  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  // Fetch token from API
  csrfTokenFetchPromise = (async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include', // Include cookies for SameSite=Lax
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.statusText}`);
      }

      const data = await response.json();
      csrfTokenCache = data.csrf_token;
      return csrfTokenCache;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch CSRF token:', error);
      // Return null to allow fallback to cookie reading
      return null;
    } finally {
      csrfTokenFetchPromise = null;
    }
  })();

  return csrfTokenFetchPromise;
};

/**
 * Clear CSRF token cache (call this on logout or when token might be invalid)
 */
export const clearCsrfTokenCache = () => {
  csrfTokenCache = null;
  csrfTokenFetchPromise = null;
};

// Helper function to extract tenant from URL (uses tenant service with security validation)
const getTenantFromUrl = () => {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const search = window.location.search;

  // Use tenant service for extraction with security validation
  try {
    const { tenant } = getTenantFromUrlService(hostname, search);
    return tenant;
  } catch (error) {
    return null;
  }
};

/**
 * Validate and get tenant alias
 * Priority: AuthContext (single source of truth) > URL (with validation) > Config default
 *
 * AuthContext is the single source of truth. It reads from localStorage on initialization
 * and provides the tenant data to all components.
 *
 * @param {Function} authContextGetter - Optional: getTenantAlias function from AuthContext
 * @throws {ConfigurationError} If tenant alias cannot be determined
 */
const getTenantAlias = (authContextGetter = null) => {
  // Priority 1: Use AuthContext getter if provided (single source of truth)
  if (authContextGetter && typeof authContextGetter === 'function') {
    try {
      const tenant = authContextGetter();
      if (tenant) {
        return tenant;
      }
    } catch (error) {
      // Error getting tenant from AuthContext
    }
  }

  // Priority 2: Use global tenant getter (set by AuthContext) - single source of truth
  if (globalTenantGetter && typeof globalTenantGetter === 'function') {
    try {
      const tenant = globalTenantGetter();
      if (tenant) {
        // globalTenantGetter returns the alias string, not the full tenant object
        return typeof tenant === 'string'
          ? tenant
          : tenant.alias || tenant.tenant_id;
      }
    } catch (error) {
      // Error getting tenant from global getter
    }
  }

  // Priority 3: Fallback via authService (in case AuthContext isn't ready yet)
  // This handles timing issues where API calls happen before AuthContext initializes
  // Use authService instead of direct localStorage access for consistency
  try {
    const cachedTenant = authService.getTenant();
    if (cachedTenant) {
      const tenantAlias =
        typeof cachedTenant === 'string'
          ? cachedTenant
          : cachedTenant.alias || cachedTenant.tenant_id;
      if (tenantAlias) {
        return tenantAlias;
      }
    }
  } catch (error) {
    // Error reading tenant from authService
  }

  // Priority 4: Extract from URL (with security validation via tenant service)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // For localhost, use config default if no tenant in URL
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const tenantFromUrl = getTenantFromUrl();
      if (tenantFromUrl) {
        return tenantFromUrl;
      }
      // Fallback to config default for localhost
      const defaultTenant = config?.defaults?.tenantAlias;
      if (defaultTenant) {
        return defaultTenant;
      }
    } else {
      // Production: extract from URL with validation
      const tenantFromUrl = getTenantFromUrl();
      if (tenantFromUrl) {
        return tenantFromUrl;
      }
    }
  }

  // Last resort: config default (but this should rarely be used)
  const tenantFromConfig = config?.defaults?.tenantAlias;
  if (tenantFromConfig) {
    return tenantFromConfig;
  }

  // NO fallback - tenant alias must come from authService (localStorage) or URL
  const errorMessage =
    'Tenant alias cannot be determined. ' +
    'Please log in to retrieve your tenant alias from localStorage, ' +
    'or ensure tenant is in URL subdomain/query param. ' +
    'No environment variable fallback is allowed to prevent using wrong tenant database.';

  throw new ConfigurationError(errorMessage, 'tenant_alias');
};

// Create headers for API requests
// @param {Function} authContextGetter - Optional: getTenantAlias function from AuthContext
export const createApiHeaders = (authContextGetter = null) => {
  try {
    // Extract getters from authContextGetter (can be object with both functions, or just getTenantAlias)
    // eslint-disable-next-line no-unused-vars
    const getTenantAliasFn =
      authContextGetter &&
      typeof authContextGetter === 'object' &&
      authContextGetter.getTenantAlias
        ? authContextGetter.getTenantAlias
        : authContextGetter; // Fallback: assume it's getTenantAlias function

    // COMMENTED OUT: API key reading from AWS secrets - we now only use Bearer token (JWT)
    // const getApiKeyFn =
    //   authContextGetter &&
    //   typeof authContextGetter === 'object' &&
    //   authContextGetter.getApiKey
    //     ? authContextGetter.getApiKey
    //     : null;

    // Build headers object
    // Backend TenantMiddleware can extract tenant_alias from multiple sources:
    // 1. JWT token (if valid) - preferred for logged-in users
    // 2. X-API-Tenant-Alias header - fallback when JWT doesn't contain tenant_alias
    const headers = {
      'Content-Type': 'application/json',
    };

    // Get tenant alias (always try to get it for backward compatibility)
    let tenantAlias = null;
    try {
      tenantAlias = getTenantAlias(getTenantAliasFn);
    } catch (error) {
      // If we can't get tenant alias, backend will try to extract from other sources
      // (subdomain, query param, JWT token, etc.)
    }

    // Bearer token (JWT) - primary authentication method for logged-in users
    // Backend uses JWT to extract user_context (user_id, email, role, groups)
    // AND can extract tenant_alias from JWT payload for routing
    //
    // IMPORTANT: Directly access localStorage to avoid circular dependency issues
    // authService.getToken() can have initialization issues in production builds
    // due to circular dependency between api.js and auth.service.js
    let token = null;
    try {
      // Try to get token via authService first (preferred method)
      if (authService && typeof authService.getToken === 'function') {
        token = authService.getToken();
      }

      // Fallback: Direct localStorage access if authService is not ready
      // This handles cases where circular dependency causes initialization issues
      if (
        !token &&
        typeof window !== 'undefined' &&
        typeof localStorage !== 'undefined'
      ) {
        const tokenKey = authService?.tokenKey || 'llamasee_auth_token';
        const storedToken = localStorage.getItem(tokenKey);
        if (storedToken) {
          token = storedToken;
          // Log in production to help diagnose if this fallback is being used
          if (
            process.env.NODE_ENV === 'production' ||
            process.env.REACT_APP_ENVIRONMENT === 'production'
          ) {
            console.warn(
              '⚠️ Used localStorage fallback to get token (authService may not be initialized)'
            );
          }
        }
      }
    } catch (error) {
      // If both methods fail, log error but continue without token
      if (
        typeof window !== 'undefined' &&
        (process.env.NODE_ENV === 'production' ||
          process.env.REACT_APP_ENVIRONMENT === 'production')
      ) {
        console.error('❌ Error retrieving JWT token:', error);
      }
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // When JWT is present, backend prefers tenant_alias from JWT payload
      // But we still send X-API-Tenant-Alias header as fallback for:
      // - Invalid/expired JWT tokens
      // - Backward compatibility with services that expect the header
      if (tenantAlias) {
        headers['X-API-Tenant-Alias'] = tenantAlias;
      }
    } else {
      // No JWT token - send tenant alias in header as fallback
      // Note: Backend requires JWT token for authentication - requests without JWT will be rejected
      // Log warning in production to help diagnose missing token issues
      if (
        typeof window !== 'undefined' &&
        (process.env.NODE_ENV === 'production' ||
          process.env.REACT_APP_ENVIRONMENT === 'production')
      ) {
        const tokenKey = authService?.tokenKey || 'llamasee_auth_token';
        console.warn(
          '⚠️ No JWT token found in localStorage - request will be unauthenticated',
          {
            tokenKey: tokenKey,
            authServiceReady: !!authService,
            hasGetTokenMethod: !!(
              authService && typeof authService.getToken === 'function'
            ),
            hasLocalStorage: typeof localStorage !== 'undefined',
            localStorageKeys:
              typeof localStorage !== 'undefined'
                ? Object.keys(localStorage).filter(
                    key => key.includes('token') || key.includes('auth')
                  )
                : [],
            directTokenCheck:
              typeof localStorage !== 'undefined'
                ? localStorage.getItem(tokenKey)
                : null,
          }
        );
      }
      if (tenantAlias) {
        headers['X-API-Tenant-Alias'] = tenantAlias;
      }
    }

    // CSRF token (Double Submit Cookie pattern)
    // Priority:
    // 1. Use cached token from API endpoint (most reliable for cross-origin)
    // 2. Fallback to reading from cookie (for same-origin requests)
    if (typeof document !== 'undefined') {
      try {
        // Try cached token first (from GET /api/v1/auth/csrf-token)
        if (csrfTokenCache) {
          headers['X-CSRF-Token'] = csrfTokenCache;
        } else {
          // Fallback: Read from cookie (may not work on cross-origin POST)
          const match = document.cookie.match(
            /(?:^|;\s*)fastapi-csrf-token=([^;]+)/
          );
          if (match && match[1]) {
            headers['X-CSRF-Token'] = decodeURIComponent(match[1]);
          }
        }
      } catch (error) {
        // If we can't read cookies or cache, skip CSRF header
        // Backend will reject unsafe requests or allow API key bypass
      }
    }

    return headers;
  } catch (error) {
    throw error;
  }
};

// Debug information - this will be visible in the browser console
const debugInfo = {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'NOT_SET',
  API_BASE_URL: (() => {
    try {
      return getApiBaseUrl();
    } catch (e) {
      return 'NOT_SET (error: ' + e.message + ')';
    }
  })(),
  HOSTNAME:
    typeof window !== 'undefined' ? window.location.hostname : 'server-side',
  HREF: typeof window !== 'undefined' ? window.location.href : 'server-side',
};

export const API_CONFIG = {
  // @deprecated Use getApiBaseUrl() instead. This getter will be removed in a future version.
  get BASE_URL() {
    // API_CONFIG.BASE_URL is deprecated. Use getApiBaseUrl() from config/api.js
    return getApiBaseUrl();
  },
  getBaseUrl: getApiBaseUrl,
  ENDPOINTS: {
    // Core endpoints (kept for potential future use)
    HEALTH: '/health',
    VERSION: '/version',
    ROOT: '/',
    DATABASE_STATUS: '/database/status',
  },
  DEBUG: debugInfo,
};

// Helper function to build full API URLs
export const buildApiUrl = endpoint => {
  const baseUrl = getApiBaseUrl(); // Validates configuration
  // Ensure base URL ends with slash and endpoint doesn't start with slash
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${normalizedBaseUrl}${cleanEndpoint}`;
};

// Export individual endpoint URLs
export const API_URLS = {
  // Authentication endpoints
  AUTH_VERIFY: buildApiUrl('/api/v1/auth/verify'),
  AUTH_LOGOUT: buildApiUrl('/api/v1/auth/logout'),
};

// Request deduplication (prevents duplicate simultaneous requests)
const pendingRequests = new Map();

// Global abort controller for cleanup
let globalAbortController = null;

// Enhanced fetch function with custom headers and request deduplication
export const apiFetch = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const method = options.method || 'GET';

  // Get timeout from options or use default
  // Support both 'timeout' (milliseconds) and 'timeoutMs' (for backward compatibility)
  // Default: 3 minutes (180 seconds) for most requests
  // Template execute endpoints should explicitly pass REQUEST_TIMEOUT_LLM_EXECUTION (300 seconds)
  const timeout =
    options.timeout || options.timeoutMs || REQUEST_TIMEOUT_DEFAULT_API; // 180 seconds (3 minutes) default

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Create cache key for GET requests only
  const cacheKey =
    method === 'GET'
      ? `${method}:${url}:${JSON.stringify(options.headers || {})}`
      : null;

  // For GET requests, check if we already have a pending request (deduplication)
  if (cacheKey && pendingRequests.has(cacheKey)) {
    // If there's a pending request, wait for it to avoid duplicate API calls
    clearTimeout(timeoutId);
    return pendingRequests.get(cacheKey);
  }

  const baseHeaders = createApiHeaders();

  // DEBUG: Log baseHeaders to see what createApiHeaders returns
  if (
    typeof window !== 'undefined' &&
    (process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_ENVIRONMENT === 'production')
  ) {
    const baseHasApiKey = !!(
      baseHeaders['X-API-Key'] || baseHeaders['x-api-key']
    );
    if (baseHasApiKey) {
      console.error(
        '❌ CRITICAL: createApiHeaders() returned X-API-Key header!',
        {
          endpoint,
          baseHeadersKeys: Object.keys(baseHeaders),
          hasXApiKey: !!baseHeaders['X-API-Key'],
          hasXApiKeyLower: !!baseHeaders['x-api-key'],
        }
      );
    }
  }

  const headers = {
    ...baseHeaders,
    ...options.headers,
  };

  // DEBUG: Log if options.headers contains API key
  if (
    typeof window !== 'undefined' &&
    (process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_ENVIRONMENT === 'production')
  ) {
    const optionsHasApiKey = !!(
      (options.headers && options.headers['X-API-Key']) ||
      (options.headers && options.headers['x-api-key'])
    );
    if (optionsHasApiKey) {
      console.error('❌ CRITICAL: options.headers contains X-API-Key header!', {
        endpoint,
        optionsHeadersKeys: options.headers ? Object.keys(options.headers) : [],
      });
    }
  }

  // CRITICAL: Explicitly remove X-API-Key header if it exists
  // This ensures API keys are NEVER sent, even if accidentally added via options.headers
  // Production should only use JWT authentication
  const hadApiKeyBeforeRemoval = !!(
    headers['X-API-Key'] || headers['x-api-key']
  );
  if (hadApiKeyBeforeRemoval) {
    delete headers['X-API-Key'];
    delete headers['x-api-key'];
    // Log error in production to catch accidental API key usage
    if (
      process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_ENVIRONMENT === 'production'
    ) {
      console.error(
        '❌ SECURITY: X-API-Key header was detected and removed from request. Only JWT authentication is allowed.',
        {
          endpoint,
          url,
          source: 'options.headers or baseHeaders',
          baseHeadersKeys: Object.keys(baseHeaders),
          optionsHeadersKeys: options.headers
            ? Object.keys(options.headers)
            : [],
          finalHeadersKeys: Object.keys(headers),
        }
      );
    }
  }

  // DEBUG: Verify API key was removed
  if (
    typeof window !== 'undefined' &&
    (process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_ENVIRONMENT === 'production')
  ) {
    const stillHasApiKey = !!(headers['X-API-Key'] || headers['x-api-key']);
    if (stillHasApiKey) {
      console.error(
        '❌ CRITICAL ERROR: X-API-Key header still present after removal attempt!',
        {
          endpoint,
          url,
          headers: Object.keys(headers),
        }
      );
    }
  }

  // Debug: Log authentication method being used (development and production)
  const hasJWT =
    !!headers['Authorization'] &&
    headers['Authorization'].startsWith('Bearer ');
  const hasApiKey = !!headers['X-API-Key'];

  if (
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_ENVIRONMENT === 'development'
  ) {
    if (hasApiKey && hasJWT) {
      console.warn(
        '⚠️ Both JWT and X-API-Key headers present - backend will prefer JWT'
      );
    } else if (hasApiKey && !hasJWT) {
      console.warn(
        '⚠️ Only X-API-Key header present (no JWT) - using API key authentication'
      );
    } else if (hasJWT && !hasApiKey) {
      console.log('✅ Using JWT authentication (no API key)');
    } else if (!hasJWT && !hasApiKey) {
      console.warn(
        '⚠️ No authentication headers found - request will be unauthenticated'
      );
    }
  } else if (
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_ENVIRONMENT === 'production'
  ) {
    // In production, only log warnings for missing authentication
    if (!hasJWT && !hasApiKey) {
      console.error(
        '❌ PRODUCTION ERROR: No authentication headers found in request',
        {
          endpoint,
          hasAuthorization: !!headers['Authorization'],
          hasApiKey: hasApiKey,
          tokenFromService: !!authService.getToken(),
        }
      );
    }
  }

  // Handle abort signals (timeout + global)
  // If global abort controller exists, listen to both signals
  let finalSignal = controller.signal;
  if (globalAbortController?.signal) {
    // Create a combined signal that aborts if either signal aborts
    const combinedController = new AbortController();
    const abortHandler = () => combinedController.abort();
    controller.signal.addEventListener('abort', abortHandler);
    globalAbortController.signal.addEventListener('abort', abortHandler);
    finalSignal = combinedController.signal;
  }

  // FINAL CHECK: Ensure API key is not in headers before creating config
  // This is a last line of defense - check again right before fetch
  if (headers['X-API-Key'] || headers['x-api-key']) {
    delete headers['X-API-Key'];
    delete headers['x-api-key'];
    if (
      typeof window !== 'undefined' &&
      (process.env.NODE_ENV === 'production' ||
        process.env.REACT_APP_ENVIRONMENT === 'production')
    ) {
      console.error(
        '❌ CRITICAL: X-API-Key header detected in final check before fetch! Removing it.',
        {
          endpoint,
          url,
        }
      );
    }
  }

  const config = {
    ...options,
    headers,
    // Add mode and credentials to handle CORS properly
    mode: 'cors',
    credentials: 'include', // Include cookies for CSRF token validation
    // Add redirect handling
    redirect: 'manual',
    // Add abort signal (timeout + global if applicable)
    signal: finalSignal,
  };

  // FINAL VERIFICATION: Check config.headers one more time before fetch
  if (
    typeof window !== 'undefined' &&
    (process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_ENVIRONMENT === 'production')
  ) {
    const configHasApiKey = !!(
      (config.headers && config.headers['X-API-Key']) ||
      (config.headers && config.headers['x-api-key'])
    );
    if (configHasApiKey) {
      console.error(
        '❌ CRITICAL ERROR: X-API-Key header found in config.headers right before fetch!',
        {
          endpoint,
          url,
          configHeadersKeys: config.headers ? Object.keys(config.headers) : [],
        }
      );
      // Remove it one more time as emergency fallback
      if (config.headers) {
        delete config.headers['X-API-Key'];
        delete config.headers['x-api-key'];
      }
    }
  }

  // Debug logging removed for production

  // Create promise and store it for deduplication
  const fetchPromise = (async () => {
    try {
      const response = await fetch(url, config);

      // Clear timeout on successful response start
      clearTimeout(timeoutId);

      // Handle status 0 (request failed before getting a response)
      // This typically indicates a network error, CORS issue, or server not running
      if (response.status === 0) {
        throw new Error(
          `Network error: Unable to connect to API at ${url}. ` +
            `This usually means the server is not running, there's a CORS issue, or the connection was refused.`
        );
      }

      // Handle redirects manually
      if (response.type === 'opaqueredirect' || response.redirected) {
        // API Redirect detected
        // For opaque redirects, we can't access the redirect URL due to CORS
        // Only throw error if we're on HTTPS and being redirected to HTTP
        const originalUrlIsHttps = url.startsWith('https://');
        if (
          response.url &&
          response.url.startsWith('http://') &&
          originalUrlIsHttps
        ) {
          // Redirected from HTTPS to HTTP, this will cause CORS issues
          throw new Error(
            'CORS: Server redirected from HTTPS to HTTP, which is not allowed'
          );
        }
        // For localhost HTTP or opaque redirects, let the browser handle it
        // by checking status codes below
      }

      // Handle redirect status codes (301, 302, 307, 308)
      if (response.status >= 300 && response.status < 400) {
        const location = response.headers.get('location');
        if (location) {
          // Follow the redirect by making a new request
          const redirectUrl = location.startsWith('http')
            ? location
            : new URL(location, url).toString();

          // Check if redirecting from HTTPS to HTTP (security issue)
          const originalUrlIsHttps = url.startsWith('https://');
          const redirectUrlIsHttp = redirectUrl.startsWith('http://');
          if (originalUrlIsHttps && redirectUrlIsHttp) {
            throw new Error(
              'CORS: Server redirected from HTTPS to HTTP, which is not allowed'
            );
          }

          return fetch(redirectUrl, {
            ...config,
            method: config.method || method,
          }).then(async redirectResponse => {
            if (!redirectResponse.ok) {
              throw new Error(
                `HTTP ${redirectResponse.status}: ${redirectResponse.statusText}`
              );
            }
            const contentType =
              redirectResponse.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              return await redirectResponse.json();
            }
            const text = await redirectResponse.text();
            try {
              return JSON.parse(text);
            } catch (_) {
              return text;
            }
          });
        }
      }

      if (!response.ok) {
        // API Response Error
        // Handle status 0 (network error, CORS issue, or connection refused)
        if (response.status === 0) {
          throw new Error(
            `Network error: Unable to connect to API at ${url}. ` +
              `This usually means the server is not running, there's a CORS issue, or the connection was refused.`
          );
        }

        // Try to extract error details from response body
        let errorDetail = response.statusText;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const errorData = await response.json();
            errorDetail =
              errorData.detail ||
              errorData.message ||
              errorData.error ||
              response.statusText;
          } else {
            const text = await response.text();
            if (text) {
              errorDetail = text;
            }
          }
        } catch (parseError) {
          // If we can't parse the error response, use status text
          errorDetail = response.statusText;
        }

        const error = new Error(`HTTP ${response.status}: ${errorDetail}`);
        error.status = response.status;
        error.response = response;
        throw error;
      }

      const contentType = response.headers.get('content-type') || '';
      const contentLength = response.headers.get('content-length');

      if (response.status === 204 || contentLength === '0') {
        return null;
      }

      if (contentType.includes('application/json')) {
        return await response.json();
      }

      // Fallback: try text
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (_) {
        return text;
      }
    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);

      // Check if it's a timeout error
      if (error.name === 'AbortError' && controller.signal.aborted) {
        const timeoutError = new Error(
          `Request timeout after ${timeout / 1000} seconds. The request took too long to complete.`
        );
        timeoutError.name = 'TimeoutError';
        timeoutError.url = url;
        timeoutError.timeout = timeout;
        throw timeoutError;
      }

      // Check if it's a ConfigurationError (authentication/configuration issue)
      if (
        error.name === 'ConfigurationError' ||
        error.constructor.name === 'ConfigurationError'
      ) {
        // Configuration error - likely missing authentication
        const enhancedError = new Error(
          `Authentication required: ${error.message || 'Please log in to access this resource.'}`
        );
        enhancedError.originalError = error;
        enhancedError.url = url;
        enhancedError.isAuthError = true;
        throw enhancedError;
      }

      // Enhanced error handling for network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        // Network error - connection failed, CORS issue, or invalid URL
        const enhancedError = new Error(
          `Network error: Unable to reach API at ${url}. Check your internet connection and ensure the API server is running.`
        );
        enhancedError.originalError = error;
        enhancedError.url = url;
        throw enhancedError;
      }
      // Re-throw other errors as-is
      throw error;
    }
  })();

  // Store pending request for deduplication
  if (cacheKey) {
    pendingRequests.set(cacheKey, fetchPromise);
  }

  try {
    const data = await fetchPromise;
    return data;
  } finally {
    // Clean up pending request
    if (cacheKey) {
      pendingRequests.delete(cacheKey);
    }
  }
};

// Legacy cleanup functions - DEPRECATED
// These are kept for backward compatibility but do nothing
export const initializeApiCleanup = () => {
  // initializeApiCleanup is deprecated and does nothing
};

export const cleanupApiRequests = () => {
  // cleanupApiRequests is deprecated and does nothing
};

// Export validation functions for use in other modules
// Note: setGlobalTenantGetter is already exported directly above
export { getApiBaseUrl, getTenantAlias, ConfigurationError };

export default API_CONFIG;
